import React, { useEffect } from 'react'
import { useConfig } from '@peracto/peracto-config'
import TextInput from '@peracto/peracto-editor-ui/dist/TextInput'
import File from '@peracto/peracto-editor-ui/dist/File'
import Select from '@peracto/peracto-editor-ui/dist/Select'
import Checkbox from '@peracto/peracto-editor-ui/dist/Checkbox'
import Slider from '@peracto/peracto-editor-ui/dist/Slider'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import startCase from 'lodash/startCase'

const ButtonsForm = ({
    onChange,
    state: {
        buttons,
        direction = 'row',
        justifyContent = 'flex-start',
        spacing = 10,
        selectedButtonIndex,
    },
}) => {
    const config = useConfig()
    const { theme, buttonVariants } = config.get('editorConfig') || []

    const variantOptions = buttonVariants
        ? Object.keys(buttonVariants)?.map(variant => {
              return {
                  label: startCase(variant),
                  value: variant,
              }
          })
        : []

    const themeColours = []

    if (theme?.colors?.brand) {
        for (const [label, colour] of Object.entries(theme.colors.brand)) {
            themeColours.push({
                label: startCase(label),
                value: colour,
            })
        }
    }

    const DEFAULT_BUTTON = {
        label: 'Click Me',
        colour: '#000',
        textColour: '#FFF',
        url: '#',
        openInNewTab: false,
        size: 'md',
        style: 'solid',
        variant: 'primary',
        width: 'auto',
        isDark: false,
    }

    const JUSTIFICATION_OPTIONS = [
        {
            label: 'Start',
            value: 'flex-start',
        },
        {
            label: 'Centre',
            value: 'center',
        },
        {
            label: 'End',
            value: 'flex-end',
        },
    ]

    if (direction === 'row') {
        JUSTIFICATION_OPTIONS.push(
            {
                label: 'Space Between',
                value: 'space-between',
            },
            {
                label: 'Space Around',
                value: 'space-around',
            }
        )
    }

    useEffect(() => {
        return () => {
            onChange({ selectedButtonIndex: null })
        }
    }, [])

    return (
        <>
            <Select
                label="Buttons Direction"
                options={[
                    {
                        label: 'Row',
                        value: 'row',
                    },
                    {
                        label: 'Column',
                        value: 'column',
                    },
                ]}
                value={direction}
                onChange={e => onChange({ direction: e.value })}
            />

            <Select
                label="Justify Buttons"
                options={JUSTIFICATION_OPTIONS}
                value={justifyContent}
                onChange={e => onChange({ justifyContent: e.value })}
            />

            <Slider
                label="Button Spacing"
                min={0}
                max={30}
                step={1}
                value={spacing}
                onChange={e => onChange({ spacing: e.target.value })}
            />

            <hr />

            {selectedButtonIndex !== null && buttons && buttons[selectedButtonIndex] && (
                <>
                    <TextInput
                        label="Button Label"
                        onChange={e => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].label = e.target.value

                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                        value={buttons[selectedButtonIndex].label}
                    />

                    <File
                        label="Button Link"
                        onChange={value => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].url = value
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                        value={buttons[selectedButtonIndex].url}
                    />

                    <Checkbox
                        label="Open in new tab"
                        checked={buttons[selectedButtonIndex].openInNewTab}
                        onChange={e => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].openInNewTab = e.target.checked
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    {variantOptions?.length > 0 ? (
                        <>
                            <Select
                                label="Button Variant"
                                options={variantOptions}
                                value={buttons[selectedButtonIndex].variant}
                                onChange={e => {
                                    const buttonsClone = [...buttons]
                                    buttonsClone[selectedButtonIndex].variant = e.value
                                    onChange({
                                        buttons: buttonsClone,
                                    })
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <Select
                                label="Button Colour"
                                options={[...themeColours]}
                                value={buttons[selectedButtonIndex].colour}
                                onChange={e => {
                                    const buttonsClone = [...buttons]
                                    buttonsClone[selectedButtonIndex].colour = e.value
                                    onChange({
                                        buttons: buttonsClone,
                                    })
                                }}
                            />

                            <Select
                                label="Text Colour"
                                options={[
                                    { label: 'White', value: '#fff' },
                                    { label: 'Black', value: '#000' },
                                    ...themeColours,
                                ]}
                                value={buttons[selectedButtonIndex].textColour}
                                onChange={e => {
                                    const buttonsClone = [...buttons]
                                    buttonsClone[selectedButtonIndex].textColour = e.value
                                    onChange({
                                        buttons: buttonsClone,
                                    })
                                }}
                            />
                        </>
                    )}

                    <Select
                        label="Button Style"
                        options={[
                            {
                                label: 'Solid',
                                value: 'solid',
                            },
                            {
                                label: 'Outline',
                                value: 'outline',
                            },
                            {
                                label: 'Link',
                                value: 'link',
                            },
                        ]}
                        value={buttons[selectedButtonIndex].style}
                        onChange={e => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].style = e.value
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    <Select
                        label="Button Width"
                        options={[
                            {
                                label: 'Auto',
                                value: 'auto',
                            },
                            {
                                label: 'Full',
                                value: 'full',
                            },
                        ]}
                        value={buttons[selectedButtonIndex].width || 'auto'}
                        onChange={e => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].width = e.value
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    <button
                        className="btn btn-sm btn-danger !w-full !mt-2"
                        onClick={e => {
                            const buttonsClone = [...buttons]

                            buttonsClone.splice(selectedButtonIndex, 1)

                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} className="!mr-2" /> Remove Button
                    </button>
                </>
            )}

            <hr className="!my-2" />

            <button
                className="btn btn-sm btn-secondary !w-full"
                onClick={e => {
                    const buttonsClone = [...buttons, DEFAULT_BUTTON]
                    onChange({
                        buttons: buttonsClone,
                    })
                }}
            >
                <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" /> Add Button
            </button>
        </>
    )
}

export default ButtonsForm
